import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { Sync } from '@mui/icons-material';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';
import { payment_status } from '../../../../services/helpers';
import { processAndSaveContractText } from '../../../../utils/processAndSaveContractText';

const CUSTOM_TYPE_LABELS = {
    custom_app: 'Aplicativo',
    custom_telemedicina: 'Telemedicina',
    custom_telepet: 'Telepet',
};

const NO_TYPE_KEY = '_none';

function formatCurrency(value) {
    return Number(value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getTypeKey(item) {
    return item?.additionalType || NO_TYPE_KEY;
}

function getTypeLabel(additionalType) {
    if (!additionalType || additionalType === NO_TYPE_KEY) return 'Outros';
    return CUSTOM_TYPE_LABELS[additionalType] || additionalType;
}

function dedupeProductKitByType(productIds, items) {
    const seenTypes = new Set();
    return productIds.filter(id => {
        const item = items.find(i => i.id === id);
        const type = getTypeKey(item);
        if (seenTypes.has(type)) return false;
        seenTypes.add(type);
        return true;
    });
}

export function Customizacao({ mutate, contract }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const confirm = useConfirm();
    const [isSyncing, setIsSyncing] = useState(false);
    const { data, isFetching } = useFetch(`additional`, { params: { paged: false, status: true, type: 'custom' } });

    const customizationOrder = contract?.customizationOrder;
    const canSyncPayment = Boolean(customizationOrder?.paymentId);
    const paymentStatus = customizationOrder?.paymentStatus;

    const handleSyncPayment = async () => {
        try {
            setIsSyncing(true);
            const { data: response } = await server.put(
                `subscription/${contract.uuid}/customization/payment/sync`,
            );
            await mutate();
            const status = response?.data?.paymentStatus;
            enqueueSnackbar(
                status
                    ? `Pagamento da customização sincronizado: ${payment_status[status] || status}`
                    : 'Pagamento da customização sincronizado com o Asaas.',
            );
        } catch (error) {
            setErrors(error);
        } finally {
            setIsSyncing(false);
        }
    };

    const { setValues, setFieldValue, isSubmitting, handleSubmit, values } = useFormik({
        initialValues: {
            product_kit: [],
        },
        validateOnChange: true,
        validateOnMount: true,
        onSubmit: async (formValues, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await confirm({
                    title: 'Salvar Customização',
                    description: 'Tem certeza que deseja salvar as customizações?',
                    confirmationText: 'Confirmo',
                });
                await server.put(`subscription/${contract.uuid}/customization`, {
                    product_kit: formValues.product_kit,
                });
                await mutate?.();
                await processAndSaveContractText(contract.uuid, { mutate });
                setSubmitting(false);
                enqueueSnackbar('Customizações salvas com sucesso');
            } catch (error) {
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    const items = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    useEffect(() => {
        if (!contract || !items.length) return;

        const customProducts =
            contract?.customizationOrder?.Order_products?.map(i => i.productId) || [];

        setValues({
            product_kit: dedupeProductKitByType(customProducts, items),
        });
    }, [contract, items, setValues]);

    const groupedItems = useMemo(() => {
        const groups = {};

        items.forEach(item => {
            const type = getTypeKey(item);
            if (!groups[type]) groups[type] = [];
            groups[type].push(item);
        });

        return Object.entries(groups).map(([type, groupItems]) => ({
            type,
            label: getTypeLabel(type),
            items: groupItems,
        }));
    }, [items]);

    const selectedItems = useMemo(
        () => items.filter(item => values.product_kit.includes(item.id)),
        [items, values.product_kit],
    );

    const selectedTotal = useMemo(
        () => selectedItems.reduce((sum, item) => sum + Number(item.price ?? 0), 0),
        [selectedItems],
    );

    function handleSelect(item) {
        const type = getTypeKey(item);
        const isSelected = values.product_kit.includes(item.id);

        if (isSelected) {
            setFieldValue(
                'product_kit',
                values.product_kit.filter(id => id !== item.id),
            );
            return;
        }

        const withoutSameType = values.product_kit.filter(id => {
            const existing = items.find(i => i.id === id);
            return getTypeKey(existing) !== type;
        });

        setFieldValue('product_kit', [...withoutSameType, item.id]);
    }

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Selecione uma customização por tipo. O valor de cada item corresponde a um pagamento avulso pela customização, e não compõe a mensalidade do clube.
            </Typography>

            {isFetching && (
                <Typography variant="body2" color="text.secondary">
                    Carregando customizações...
                </Typography>
            )}

            {!isFetching && items.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Nenhuma customização disponível no momento.
                </Typography>
            )}

            {groupedItems.map((group, groupIndex) => (
                <Stack key={group.type} spacing={1.5}>
                    {groupIndex > 0 && <Divider sx={{ my: 1 }} />}

                    <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {group.label}
                    </Typography>

                    <Stack spacing={1.5}>
                        {group.items.map(item => {
                            const isSelected = values.product_kit.includes(item.id);

                            return (
                                <Card
                                    key={item.id}
                                    variant="outlined"
                                    sx={{
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        bgcolor: isSelected ? 'action.selected' : 'background.paper',
                                        transition: 'border-color 0.2s, background-color 0.2s',
                                    }}
                                >
                                    <CardActionArea onClick={() => handleSelect(item)}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    color="primary"
                                                    tabIndex={-1}
                                                    disableRipple
                                                    sx={{ p: 0 }}
                                                />

                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        justifyContent="space-between"
                                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                        spacing={0.5}
                                                    >
                                                        <Typography variant="subtitle1" fontWeight={isSelected ? 600 : 500}>
                                                            {item.name}
                                                        </Typography>
                                                        <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                                                            {formatCurrency(item.price)}
                                                        </Typography>
                                                    </Stack>

                                                    {item.description && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            {item.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Stack>
                </Stack>
            ))}

            {selectedItems.length > 0 && (
                <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                    <CardContent>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={1}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {selectedItems.length} {selectedItems.length === 1 ? 'tipo selecionado' : 'tipos selecionados'}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Total das customizações: {formatCurrency(selectedTotal)}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {paymentStatus && (
                <Alert severity={['CONFIRMED', 'RECEIVED', 'CONCLUDED'].includes(paymentStatus) ? 'success' : 'info'}>
                    Status do pagamento: {payment_status[paymentStatus] || paymentStatus}
                </Alert>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                <Button
                    variant="outlined"
                    startIcon={<Sync />}
                    onClick={handleSyncPayment}
                    disabled={isSyncing || isSubmitting || !canSyncPayment}
                    loading={isSyncing}
                >
                    Sincronizar pagamento
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isFetching || isSyncing}
                    loading={isSubmitting}
                >
                    Salvar customizações
                </Button>
            </Stack>
        </Stack>
    );
}
